// React
import * as React from "react";
import _ from 'lodash';

// Internal components
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";

import { SettingsList } from '@Primitive/SettingsList';
import { getRowMinHeight } from '@Primitive/Typography';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import type { LocalizationMap } from '@Primitive/Localization/Localization';

import { FormattersUI } from '@Utils/Formats.UI';
import { DateFormat, Formatters } from '@Utils/Formats';
import { getBool, getRandomSmallKey, isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';

import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { WebThingPortType } from '@API/WebNodeTypes';
import { WebThing, WebThingPort } from '@API/WebThing';
// =======================================================================================================
export interface LabelData {
    Labels: ILabel[];
    FillWidget: Boolean;
    IsSingleton?: boolean;
    OddRowBackgroundColor: string;
    EvenRowBackgroundColor: string;
    dateFormat: DateFormat;
}
// ------------------------------------------------------------------------------------------------------
export interface ILabel {
    Key: string;
    Value: string;
    type: LabelType;
}
// ------------------------------------------------------------------------------------------------------
export enum LabelType {
    text = 0,
    boolean = 1,
    color = 2,
    datetime = 3
}
// ------------------------------------------------------------------------------------------------------
export class Label extends React.Component<WidgetsThingsStructs.PartialIWidgetThingsProps<LabelData>, any> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<LabelData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------------
    get containerStyle(): React.CSSProperties {
        return { padding: WidgetsThingsStructs.defaultWidgetContainerPadding };
    }
    // ---------------------------------------------------------------------------------------------------
    get useDefaultStripes(): boolean {
        return _.isEmpty(this.props.WidgetData.EvenRowBackgroundColor) &&
            _.isEmpty(this.props.WidgetData.OddRowBackgroundColor);
    }
    // ---------------------------------------------------------------------------------------------------
    renderLabelValue(value: any, type: LabelType): string | React.ReactElement<any> {
        // Render label's value depending on it's type.
        switch (type) {
            case LabelType.text:
                return value;

            case LabelType.boolean:
                return FormattersUI.boolean(getBool(value));

            case LabelType.color:
                return FormattersUI.color(value);

            case LabelType.datetime:
                return Formatters.datetime(value, this.props.WidgetData.dateFormat);

            default:
                return value;
        }
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        const useDefaultStripes: boolean = this.useDefaultStripes;

        let minHeight = undefined;
        if (this.props.WidgetData.FillWidget)
            minHeight = getRowMinHeight(
                this.props.WidgetData.Labels.length,
                this.props.height,
                48,
                0
            );

        return (
            <div style={this.containerStyle}>
                <table style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    borderSpacing: "0px",
                    tableLayout: "fixed"
                }}>
                    <tbody>
                        {_.map(this.props.WidgetData.Labels, (l: ILabel, i: number) => {

                            // Get port and set cell's style.
                            const port = (_.has(this.props.PortsStates, l.Key)) ? this.props.PortsStates[l.Key] : undefined;
                            const tdStyle: React.CSSProperties = !useDefaultStripes
                                ? {
                                    backgroundColor: i % 2 == 0
                                        ? this.props.WidgetData.EvenRowBackgroundColor
                                        : this.props.WidgetData.OddRowBackgroundColor,
                                    paddingLeft: 12,
                                    paddingRight: 12,
                                    height: minHeight
                                }
                                : {
                                    height: minHeight,
                                    paddingLeft: 12,
                                    paddingRight: 12
                                };

                            // Get label based on `LocalizationMap`
                            let label = this.props.LocalizationMap[l.Key];
                            if (!isDefined(label) || IsNullOrEmpty(label))
                                label = l.Value;

                            return (
                                <tr key={'row-' + l.Key}
                                    style={{
                                        height: 48,
                                        borderBottom: "1px solid rgb(224, 224, 224)"
                                    }}
                                >
                                    <td
                                        key={'row-column-1' + l.Key}
                                        style={tdStyle}
                                    >
                                        {label}
                                    </td>
                                    <td
                                        key={'row-column-2' + l.Key}
                                        style={tdStyle}
                                    >
                                        {/* Render label's value based on it's type. */}
                                        {(port != undefined) ? this.renderLabelValue(port.State, l.type) : ""}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }
}

// =======================================================================================================

export interface LabelWidgetSettingsState {
    WidgetData: LabelData;
}

export class LabelWidgetSettings extends React.Component<IWidgetSettingsProps<LabelData>, LabelWidgetSettingsState> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<LabelData>) {
        super(props);

        this.state = {
            WidgetData: _.cloneDeep(this.props.WidgetData)
        };
    }
    // ---------------------------------------------------------------------------------------------------
    private get _isSingleton(): boolean {
        return _.isBoolean(this.props.WidgetData.IsSingleton) && this.props.WidgetData.IsSingleton;
    }
    // ------------------------------------------------------------------------------------------------
    render() {

        let settings: SettingsGroup<LabelData>[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: 'Labels',
                        tooltip: "Specifies the label's name and type from the supported ones (text, boolean, color, Datetime).",
                        dataPath: 'Labels',
                        isHidden: (data: LabelData) => this._isSingleton,
                        itemsInfo: [
                            {
                                label: "Name",
                                type: SettingsType.String,
                                dataPath: "Value",
                            },
                            {
                                label: "Type",
                                type: SettingsType.DropDown,
                                dataPath: "type",
                                items: [
                                    { value: LabelType.text, label: 'Text' },
                                    { value: LabelType.boolean, label: 'Boolean' },
                                    { value: LabelType.color, label: 'Color' },
                                    { value: LabelType.datetime, label: 'Datetime' }
                                ]
                            }
                        ],
                        createNewItem: (data) => {
                            return {
                                Key: WebNode
                                    .GetInstanceKey(this.props.NodeKey)
                                    .GetThing(this.props.ThingKey)
                                    .GetNextGeneratedPortKey(),
                                Value: 'New Label',
                                type: LabelType.text
                            }
                        }
                    },
                    {
                        type: SettingsType.String,
                        label: 'Label',
                        dataPath: "Labels[0].Value",
                        isHidden: (data: LabelData) => !this._isSingleton
                    },
                    {
                        type: SettingsType.DropDown,
                        label: 'Type',
                        dataPath: "Labels[0].type",
                        items: [
                            { value: LabelType.text, label: 'Text' },
                            { value: LabelType.boolean, label: 'Boolean' },
                            { value: LabelType.color, label: 'Color' },
                            { value: LabelType.datetime, label: 'Datetime' }
                        ],
                        isHidden: (data: LabelData) => !this._isSingleton
                    },
                    {
                        type: SettingsType.DropDown,
                        label: 'Date Format',
                        tooltip: 'Defines whether the Date Format will use Short, Long, ISO or ISO ms formats.',
                        dataPath: "dateFormat",
                        items: [
                            { value: DateFormat.Short, label: 'Short' },
                            { value: DateFormat.Long, label: 'Long' },
                            { value: DateFormat.ISO, label: 'ISO' },
                            { value: DateFormat.ISOMS, label: 'ISO ms' }
                        ]
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Even Rows Color',
                        tooltip: 'Specifies the background color of even rows.',
                        dataPath: "EvenRowBackgroundColor",
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Odd Rows Color',
                        tooltip: 'Specifies the background color of odd rows.',
                        dataPath: "OddRowBackgroundColor",
                        isHidden: (data: LabelData) => this._isSingleton
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Fill Space",
                        tooltip: "Fills the widget's available space.",
                        dataPath: "FillWidget"
                    }
                ]
            }
        ]

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                this.setState({ WidgetData: data },
                    () => {
                        if (isDefined(this.props.onWidgetDataUpdate))
                            this.props.onWidgetDataUpdate(this.state.WidgetData);
                    }
                )
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}

// =======================================================================================================
// Label Description interface
export class LabelWidget extends IWidgetThingsClass<LabelData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): React.ReactNode {
        return <Label />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <LabelWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let wData = this.WidgetData;
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(this.ThingKey);

        // Clean old ports
        thing.Ports = {};

        // Add the ports
        _.forEach(wData.Labels, (label, index) => {
            thing.AddPort(new WebThingPort(
                {
                    Name: label.Value,
                    PortKey: label.Key,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: "Label",
                    State: "Value"
                },
                WebThingPortType.Data
            ), false);
        });

        webNode.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        let wData = this.WidgetData;

        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [];
        _.forEach(wData.Labels, (label, index) => {
            ports.push(new WebThingPort(
                {
                    Name: label.Value,
                    PortKey: label.Key,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: "Label",
                    State: "Value"
                },
                WebThingPortType.Data
            ));
        });

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): LabelData {
        return {
            Labels: [],
            FillWidget: false,
            IsSingleton: false,
            dateFormat: DateFormat.Short,
            EvenRowBackgroundColor: '#7fdde9',
            OddRowBackgroundColor: '#ffffff'
        };
    }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: LabelData = LabelWidget.GetDefaultWidgetDataStatic();

        // fill sample labels
        data.Labels = [
            { Key: "1_" + getRandomSmallKey(), Value: "Label 1", type: LabelType.text },
            { Key: "2_" + getRandomSmallKey(), Value: "Label 2", type: LabelType.text },
            { Key: "3_" + getRandomSmallKey(), Value: "Label 3", type: LabelType.text }
        ];

        return {
            Title: "Labels",
            WidgetTitle: "",
            Key: "LabelWidget",
            Type: "LabelWidget",
            Data: data,
            Description: "Label widget",
            MinH: 3,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/png/label.png"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public static MigrateDefaultWidgetDataStatic(data: LabelData): LabelData {
        // Define date format if it hasn't been set.
        if (!isDefined(data['dateFormat']))
            data.dateFormat = DateFormat.Short;

        // Define label type if it hasn't been set.
        if (data.Labels.length > 0 && !isDefined(data.Labels[0].type))
            _.forEach(data.Labels, (l: ILabel) => l.type = LabelType.text);

        return data;
    }
    // ------------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: LabelData): LabelData {
        return LabelWidget.MigrateDefaultWidgetDataStatic(data);
    }
    // ------------------------------------------------------------------------------------------------
    public static GetLocalizationDictionaryStatic(widgetData: LabelData): LocalizationMap {
        const res = {};
        _.forEach(widgetData.Labels, (label, index) => {
            res[label.Key] = label.Value;
        });
        return res;
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        const res = LabelWidget.GetLocalizationDictionaryStatic(this.WidgetData);

        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    };
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================